import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Flame, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';

const formatApiError = (detail) => {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword(email);
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] p-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Flame className="w-16 h-16 text-[#E3000F] mx-auto mb-4" />
          <h1 className="text-3xl font-bold uppercase tracking-tighter" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            VVF Interventi
          </h1>
        </div>

        <div className="bg-[#121212] border border-[#27272A] p-8">
          <h2 className="text-2xl font-semibold uppercase tracking-tight mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Password dimenticata
          </h2>
          <p className="text-[#A1A1AA] text-sm mb-6">
            Inserisci la tua email per ricevere un link di reset.
          </p>

          {error && (
            <div className="bg-[#E3000F]/10 border border-[#E3000F]/30 p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#E3000F] flex-shrink-0 mt-0.5" />
              <p className="text-[#E3000F] text-sm">{error}</p>
            </div>
          )}

          {success ? (
            <div className="bg-[#009246]/10 border border-[#009246]/30 p-6 flex flex-col items-center gap-4">
              <CheckCircle className="w-12 h-12 text-[#009246]" />
              <div className="text-center">
                <p className="text-white font-medium mb-2">Email inviata!</p>
                <p className="text-[#A1A1AA] text-sm">
                  Se l'indirizzo email è registrato, riceverai un link per reimpostare la password.
                </p>
              </div>
              <Link to="/login" className="text-[#E3000F] hover:underline text-sm mt-2">
                Torna al login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="form-label">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] rounded-none h-12 text-white focus:border-[#E3000F]"
                  placeholder="email@esempio.it"
                  required
                  data-testid="forgot-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E3000F] hover:bg-[#CC000D] text-white font-semibold rounded-none h-12 uppercase tracking-wide border-2 border-transparent hover:border-[#E3000F] hover:bg-transparent transition-all"
                data-testid="forgot-submit-btn"
              >
                {loading ? 'Invio...' : 'Invia link di reset'}
              </Button>
            </form>
          )}

          <div className="mt-6">
            <Link to="/login" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white text-sm transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Torna al login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
